import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu, TrendingUp, Building2, CreditCard, PiggyBank, Info, Phone, BookOpen, HelpCircle, Home, ShieldAlert, Calculator } from "lucide-react";
import { triggerHaptic } from "@/utils/haptics";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useState } from "react";
import { 
  MortgagePage, 
  PrivateLoanPage, 
  BusinessLoanPage, 
  LoanWithoutUCPage, 
  KlarsprakPage, 
  ContactPage,
  MortgageCalculatorPage,
  InterestRateCalculatorPage,
  KALPCalculatorPage
} from "@/routes/preloadableRoutes";

export const MobileNavSheet = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();

  const menuItems = [
    { icon: Home, label: "Bolån", path: `/${currentLanguage}/bolan`, preload: MortgagePage.preload },
    { icon: TrendingUp, label: "Privatlån", path: `/${currentLanguage}/privatlan`, preload: PrivateLoanPage.preload },
    { icon: ShieldAlert, label: "Lån utan UC", path: `/${currentLanguage}/lana-utan-uc`, preload: LoanWithoutUCPage.preload },
    { icon: Building2, label: "Företagslån", path: `/${currentLanguage}/foretagslan`, preload: BusinessLoanPage.preload },
  ];

  const toolsLinks = [
    { icon: Calculator, label: "Bolånekalkylator", path: `/${currentLanguage}/verktyg/bolanekalkylator`, preload: MortgageCalculatorPage.preload },
    { icon: Calculator, label: "Räntekalkylator", path: `/${currentLanguage}/verktyg/rantekalkylator`, preload: InterestRateCalculatorPage.preload },
    { icon: Calculator, label: "KALP-kalkylator", path: `/${currentLanguage}/verktyg/kalp-kalkylator`, preload: KALPCalculatorPage.preload },
  ];

  const quickLinks = [
    { icon: BookOpen, label: "Klarspråk", path: `/${currentLanguage}/klarsprak`, preload: KlarsprakPage.preload },
    { icon: Phone, label: "Kontakta oss", path: `/${currentLanguage}/kontakt`, preload: ContactPage.preload },
    { icon: HelpCircle, label: "Vanliga frågor", path: `/${currentLanguage}/faq` },
  ];

  const handleItemClick = (path: string) => {
    triggerHaptic('light');
    setOpen(false);
    
    // Navigate after short delay - ScrollToTop handles the scroll
    setTimeout(() => {
      navigate(path);
    }, 50);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden p-3 h-12 w-12"
          onClick={() => triggerHaptic('medium')}
          aria-label="Öppna meny"
        >
          <Menu className="h-7 w-7" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="p-6 pb-4 border-b text-left">
          <SheetTitle className="text-xl font-bold text-left">Klarfinans</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Main Menu */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Produkter
              </h3>
              <div className="space-y-1">
                {menuItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleItemClick(item.path)}
                    onTouchStart={() => item.preload?.()}
                    className="w-full flex items-center justify-start gap-3 p-3 rounded-lg active:bg-primary/10 transition-colors min-h-touch touch-manipulation"
                  >
                    <item.icon className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="font-medium text-left">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Tools */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Verktyg
              </h3>
              <div className="space-y-1">
                {toolsLinks.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleItemClick(item.path)}
                    onTouchStart={() => item.preload?.()}
                    className="w-full flex items-center justify-start gap-3 p-3 rounded-lg active:bg-primary/10 transition-colors min-h-touch touch-manipulation"
                  >
                    <item.icon className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="font-medium text-left">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Snabblänkar
              </h3>
              <div className="space-y-1">
                {quickLinks.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleItemClick(item.path)}
                    onTouchStart={() => item.preload?.()}
                    className="w-full flex items-center justify-start gap-3 p-3 rounded-lg active:bg-primary/10 transition-colors min-h-touch touch-manipulation"
                  >
                    <item.icon className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="font-medium text-left">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

          {/* Footer */}
          <div className="border-t p-6">
            <Button
              className="w-full min-h-touch"
              onClick={() => {
                triggerHaptic('medium');
                handleItemClick(`/${currentLanguage}/privatlan`);
              }}
            >
              <TrendingUp className="mr-2 h-5 w-5" />
              Jämför lån nu
            </Button>
          </div>
      </SheetContent>
    </Sheet>
  );
};
